const SAMPLE_RATE = 16_000
const MAX_SOCKET_BUFFER_BYTES = 16 * 1024
const MAX_PLAYBACK_BACKLOG_SECONDS = 0.25

type MediaFrame = { event?: string; payload?: string; message?: string }

export class WebPcmMedia {
  private readonly context = new AudioContext({ latencyHint: 'interactive' })
  private socket: WebSocket | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private processor: AudioWorkletNode | null = null
  private sink: GainNode | null = null
  private nextPlaybackAt = 0
  private readonly scheduled = new Set<AudioBufferSourceNode>()
  private closed = false
  private providerReady = false
  private speakingListener: ((speaking: boolean, level: number) => void) | null = null
  private silenceTimer: number | null = null
  private readonly readyWaiters = new Set<{ resolve: () => void; reject: (error: Error) => void }>()

  setRemoteActivityListener(listener: (speaking: boolean, level: number) => void) {
    this.speakingListener = listener
  }

  async setInputStream(stream: MediaStream) {
    if (this.context.state !== 'running') await this.context.resume()
    await this.context.audioWorklet.addModule('/voice-pcm-capture-worklet.js')
    this.disconnectInput()
    const source = this.context.createMediaStreamSource(stream)
    const processor = new AudioWorkletNode(this.context, 'devzapp-pcm-capture', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1]
    })
    const sink = this.context.createGain()
    sink.gain.value = 0
    processor.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
      if (!this.providerReady || this.socket?.readyState !== WebSocket.OPEN) return
      if (this.socket.bufferedAmount > MAX_SOCKET_BUFFER_BYTES) return
      this.socket.send(JSON.stringify({ event: 'media', payload: bytesToBase64(new Uint8Array(event.data)) }))
    }
    source.connect(processor)
    processor.connect(sink)
    sink.connect(this.context.destination)
    this.source = source
    this.processor = processor
    this.sink = sink
  }

  async connect(url: string, onUnexpectedClose: () => void) {
    if (this.context.state !== 'running') await this.context.resume()
    await new Promise<void>((resolve, reject) => {
      const socket = new WebSocket(url)
      this.socket = socket
      let opened = false
      const timeout = window.setTimeout(() => {
        socket.close()
        reject(new Error('Tempo esgotado ao conectar o áudio da ligação.'))
      }, 10_000)
      socket.onopen = () => {
        opened = true
        window.clearTimeout(timeout)
        resolve()
      }
      socket.onerror = () => {
        window.clearTimeout(timeout)
        if (!opened) reject(new Error('Não foi possível conectar o áudio da ligação.'))
      }
      socket.onmessage = event => {
        if (typeof event.data !== 'string') return
        let frame: MediaFrame
        try {
          frame = JSON.parse(event.data)
        } catch {
          return
        }
        if (frame.event === 'connected') {
          this.providerReady = true
          this.readyWaiters.forEach(waiter => waiter.resolve())
          this.readyWaiters.clear()
        } else if (frame.event === 'media' && frame.payload) {
          this.play(frame.payload)
        } else if (frame.event === 'end' || frame.event === 'ended' || frame.event === 'error') {
          socket.close()
        }
      }
      socket.onclose = () => {
        window.clearTimeout(timeout)
        if (!this.closed && opened) onUnexpectedClose()
      }
    })
  }

  async waitUntilProviderReady(timeoutMs = 120_000) {
    if (this.providerReady) return
    await new Promise<void>((resolve, reject) => {
      const waiter = {
        resolve: () => {
          window.clearTimeout(timeout)
          this.readyWaiters.delete(waiter)
          resolve()
        },
        reject
      }
      const timeout = window.setTimeout(() => {
        this.readyWaiters.delete(waiter)
        reject(new Error('A ligação não estabeleceu o canal de áudio.'))
      }, timeoutMs)
      this.readyWaiters.add(waiter)
    })
  }

  async close() {
    this.closed = true
    this.readyWaiters.forEach(waiter => waiter.reject(new Error('Ligação encerrada.')))
    this.readyWaiters.clear()
    this.disconnectInput()
    this.clearPlayback()
    this.setSpeaking(false)
    this.socket?.close(1000, 'call ended')
    this.socket = null
    await this.context.close().catch(() => undefined)
  }

  private disconnectInput() {
    if (this.processor) this.processor.port.onmessage = null
    this.source?.disconnect()
    this.processor?.disconnect()
    this.sink?.disconnect()
    this.source = null
    this.processor = null
    this.sink = null
  }

  private play(payload: string) {
    let bytes: Uint8Array
    try {
      bytes = base64ToBytes(payload)
    } catch {
      return
    }
    if (!bytes.length || bytes.length % 2) return
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    const samples = bytes.length / 2
    const buffer = this.context.createBuffer(1, samples, SAMPLE_RATE)
    const channel = buffer.getChannelData(0)
    let energy = 0
    for (let index = 0; index < samples; index += 1) {
      const sample = view.getInt16(index * 2, true) / 32768
      channel[index] = sample
      energy += sample * sample
    }
    const level = Math.sqrt(energy / samples)
    if (level >= 0.012) this.markSpeaking(level)
    const source = this.context.createBufferSource()
    source.buffer = buffer
    source.connect(this.context.destination)
    const now = this.context.currentTime
    if (this.nextPlaybackAt < now || this.nextPlaybackAt > now + MAX_PLAYBACK_BACKLOG_SECONDS) {
      if (this.nextPlaybackAt > now + MAX_PLAYBACK_BACKLOG_SECONDS) this.clearPlayback()
      this.nextPlaybackAt = now + 0.025
    }
    this.scheduled.add(source)
    source.onended = () => {
      this.scheduled.delete(source)
      source.disconnect()
    }
    source.start(this.nextPlaybackAt)
    this.nextPlaybackAt += buffer.duration
  }

  private markSpeaking(level: number) {
    this.setSpeaking(true, level)
    if (this.silenceTimer !== null) window.clearTimeout(this.silenceTimer)
    this.silenceTimer = window.setTimeout(() => this.setSpeaking(false, 0), 240)
  }

  private setSpeaking(speaking: boolean, level = 0) {
    if (!speaking && this.silenceTimer !== null) {
      window.clearTimeout(this.silenceTimer)
      this.silenceTimer = null
    }
    this.speakingListener?.(speaking, level)
  }

  private clearPlayback() {
    this.scheduled.forEach(source => {
      source.onended = null
      try {
        source.stop()
      } catch {
        // Already ended.
      }
      source.disconnect()
    })
    this.scheduled.clear()
    this.nextPlaybackAt = 0
  }
}

function bytesToBase64(bytes: Uint8Array) {
  let binary = ''
  bytes.forEach(byte => (binary += String.fromCharCode(byte)))
  return window.btoa(binary)
}

function base64ToBytes(value: string) {
  const binary = window.atob(value)
  return Uint8Array.from(binary, character => character.charCodeAt(0))
}
